import { chain } from 'lodash';
import { DateTime } from 'luxon';
import { TechnicalPointCategory } from '../../dashboard/fleet/common/enums/technical-point-category';
import { OrderPointCategory } from '../../dashboard/orders/common/enums/order-point-category';
import { OrderTimeWindowType } from '../../dashboard/orders/common/enums/order-time-window-type';
import { OrderTransitPointTimeWindowType } from '../../dashboard/orders/common/enums/order-transit-point-time-window-type';
import type { RoutePoint } from '../services/route.service';
import type { RoutePointValue } from './route.helper';
import { RoutePointValueHelper } from './route-point-value.helper';

export type DeadlineType = 'arrival' | 'departure';

export class DeadlineHelper {
  public static getChainDeadline(
    point: RoutePoint,
    points: RoutePoint[],
  ): { arrival: DateTime; departure: DateTime } | null {
    const value = RoutePointValueHelper.getValue(point);
    const pointsValue = points.map((point) =>
      RoutePointValueHelper.getValue(point),
    );

    const index = pointsValue.findIndex(({ uuid }) => uuid === value.uuid);
    if (index === -1) return null;

    const departure = DeadlineHelper.getChainDepartureDeadline(
      index,
      pointsValue,
    );
    if (!departure) return null;

    const estimatedArrivalDeadlineTime =
      RoutePointValueHelper.getEstimations(value)?.arrivalDeadlineTime;
    return {
      arrival: estimatedArrivalDeadlineTime
        ? DateTime.fromISO(estimatedArrivalDeadlineTime)
        : departure.minus({ seconds: value.serviceTime ?? 0 }),
      departure,
    };
  }

  public static getPointDeadline(
    point: RoutePoint,
  ): { arrival: DateTime; departure: DateTime } | null {
    const value = RoutePointValueHelper.getValue(point);
    if (RoutePointValueHelper.isOrderFormPoint(value)) return null;

    const departure = DeadlineHelper.getPointDepartureDeadline(value);
    if (!departure) return null;

    return {
      arrival: departure.minus({ seconds: value.serviceTime ?? 0 }),
      departure,
    };
  }

  public static getPointDepartureDeadline(point: RoutePoint): DateTime | null {
    const value = RoutePointValueHelper.getValue(point);

    const windows = RoutePointValueHelper.getTimeWindows(value)?.windows;
    if (windows?.length) {
      return (
        chain(windows)
          .map((window) => {
            if (!window.date) return null;
            if (window.timeRange?.to) {
              return DateTime.fromISO(`${window.date}T${window.timeRange.to}`);
            }
            if (!window.time) return null;

            return DateTime.fromISO(`${window.date}T${window.time}`).plus({
              seconds: value.serviceTime ?? 0,
            });
          })
          .compact()
          .maxBy((deadline) => deadline.toMillis())
          .value() ?? null
      );
    }

    const transit = RoutePointValueHelper.getTransitTimeWindows(value);
    if (transit) {
      if (transit.type === OrderTransitPointTimeWindowType.ALL_TIME) {
        return null;
      }

      return DateTime.fromISO(`${transit.date}T${transit.time}`).plus({
        seconds: value.serviceTime ?? 0,
      });
    }

    return null;
  }

  private static getChainDepartureDeadline(
    index: number,
    points: RoutePointValue[],
  ): DateTime | null {
    const point = points[index];

    const estimatedDeadlineTime =
      RoutePointValueHelper.getEstimations(point)?.departureDeadlineTime;
    if (estimatedDeadlineTime) return DateTime.fromISO(estimatedDeadlineTime);

    const getNext = (): [RoutePointValue, DateTime] | [null, null] => {
      const nextPoint = points.at(index + 1);
      if (!nextPoint) return [null, null];

      const nextDeadline = DeadlineHelper.getChainDepartureDeadline(
        index + 1,
        points,
      );
      if (!nextDeadline) return [null, null];

      return [nextPoint, nextDeadline];
    };

    switch (point.category) {
      case TechnicalPointCategory.SWAP_POINT: {
        const [nextPoint, nextDeadline] = getNext();
        if (!nextPoint || !nextDeadline) return null;

        return nextDeadline.minus({
          seconds:
            (nextPoint.serviceTime ?? 0) +
            (nextPoint.route?.summary.duration ?? 0) +
            (point.serviceTime ?? 0),
        });
      }

      case OrderPointCategory.CHECKPOINT:
      case OrderPointCategory.PICKUP: {
        const [nextPoint, nextDeadline] = getNext();
        if (!nextPoint || !nextDeadline) return null;

        const maxArrivalDeadline = nextDeadline.minus({
          seconds:
            (nextPoint.serviceTime ?? 0) +
            (nextPoint.route?.summary.duration ?? 0) +
            (point.serviceTime ?? 0),
        });

        return chain(maxArrivalDeadline)
          .thru((maxDeadline) =>
            Array.from({ length: 4 }).map((_, index) =>
              maxDeadline.minus({ days: index }),
            ),
          )
          .map((date) => {
            let openingHours;
            if (date.weekday === 7) {
              openingHours = point.businessHours.sunday;
            } else if (date.weekday === 6) {
              openingHours = point.businessHours.saturday;
            } else {
              openingHours = point.businessHours.weekday;
            }
            if (!openingHours) return null;

            const timeFrom = DateTime.fromISO(openingHours.from);
            const timeTo = DateTime.fromISO(openingHours.to);

            const from = date.set({
              hour: timeFrom.hour,
              minute: timeFrom.minute,
            });
            const to = date.set({ hour: timeTo.hour, minute: timeTo.minute });

            return { from, to };
          })
          .compact()
          .thru(
            (intervals) =>
              intervals.find(
                (interval) => maxArrivalDeadline >= interval.from,
              ) ?? intervals[0],
          )
          .thru((interval) =>
            DateTime.min(
              interval.to,
              maxArrivalDeadline.plus({ seconds: point.serviceTime ?? 0 }),
            ),
          )
          .value();
      }

      case OrderPointCategory.TRANSIT: {
        const timeWindow = point.timeWindow;

        if (timeWindow.type === OrderTransitPointTimeWindowType.ALL_TIME) {
          const [nextPoint, nextDeadline] = getNext();
          if (!nextPoint || !nextDeadline) return null;

          return nextDeadline.minus({
            seconds:
              (nextPoint.serviceTime ?? 0) +
              (nextPoint.route?.summary.duration ?? 0),
          });
        }

        if (!timeWindow.date || !timeWindow.time) return null;

        return DateTime.fromISO(`${timeWindow.date}T${timeWindow.time}`).plus({
          seconds: point.serviceTime ?? 0,
        });
      }

      default: {
        const timeWindows = point.timeWindows;
        if (!timeWindows.windows?.length) return null;

        if (
          timeWindows.type === OrderTimeWindowType.INTERVAL ||
          timeWindows.type === OrderTimeWindowType.OPENING_HOURS
        ) {
          const [nextPoint, nextDeadline] = getNext();
          const maxArrivalDeadline = nextDeadline
            ? nextDeadline.minus({
                seconds:
                  (nextPoint.serviceTime ?? 0) +
                  (nextPoint.route?.summary.duration ?? 0) +
                  (point.serviceTime ?? 0),
              })
            : null;

          return chain(timeWindows.windows)
            .map((window) =>
              window.date && window.timeRange
                ? {
                    from: DateTime.fromISO(
                      `${window.date}T${window.timeRange.from}`,
                    ),
                    to: DateTime.fromISO(
                      `${window.date}T${window.timeRange.to}`,
                    ),
                  }
                : null,
            )
            .compact()
            .orderBy((interval) => interval.to, 'desc')
            .thru((intervals) => {
              if (!maxArrivalDeadline) return intervals[0];

              return (
                intervals.find(
                  (interval) => maxArrivalDeadline >= interval.from,
                ) ?? intervals[0]
              );
            })
            .thru((interval) => {
              if (!interval) return null;
              if (!maxArrivalDeadline) return interval.to;

              return DateTime.min(
                interval.to,
                maxArrivalDeadline.plus({ seconds: point.serviceTime ?? 0 }),
              );
            })
            .value();
        }

        const [window] = timeWindows.windows;
        if (!window.date || !window.time) return null;

        return DateTime.fromISO(`${window.date}T${window.time}`).plus({
          seconds: point.serviceTime ?? 0,
        });
      }
    }
  }
}
